// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the menu, the panels and the summary.
//
// THE DISMISSAL CONTRACT is implemented once, in openPanel(), and every overlay goes through it: a
// 44px close control, a tap anywhere outside, Escape, and a backdrop that is genuinely reachable. An
// overlay with one way out is a game you cannot play, and on a phone the button that opened it is
// underneath the thing covering it.

import { CARDS, cardOf } from './cards';
import { MODES, MODE_IDS, type ModeId } from './modes';
import { rankName, rankPhrase, revealedAside, type PublicState } from './game';
import type { Award } from './game';
import { short } from './view';

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export const FOOTER_HTML = `<footer class="site-footer">
  Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
  · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
</footer>`;

export const HOW_HTML = `
  <h2>How to play</h2>
  <p>You are holding one piece of testimony. So is everyone else. Nobody sees anyone's.</p>
  <ul class="how">
    <li><b>Draw one, play one.</b> On your turn you take a card off the docket and play one of the two.
      Whatever you play happens to somebody, and it lands face up where the whole room can see it.</li>
    <li><b>Everything played is evidence.</b> The docket panel counts what is left of each rank. If
      three of the five Bailiffs are on the table, a Bailiff is now the least likely thing anyone
      holds — that is the whole game.</li>
    <li><b>A missed call still pays.</b> Name a rival's testimony and get it wrong, and you alone are
      told whether theirs is higher or lower. Two calls will usually pin somebody exactly.</li>
    <li><b>Be the last one standing</b>, or hold the strongest testimony when the docket runs dry.
      Standing still, and being right, are different skills.</li>
  </ul>
  <p class="fine">Tap a card to pick it up, tap who it is aimed at, then tap the bar — it always says
    exactly what will happen. On a keyboard: 1–2 pick a card, 1–8 name a rank, Enter commits,
    Escape clears.</p>`;

export const cardsHtml = (): string => `
  <h2>The testimony</h2>
  <p class="fine">Sixteen cards at two to four seats, twenty-two at five or six. The composition never
    changes and is never hidden — subtracting it is the game.</p>
  <ul class="clist">${CARDS.map(
    (c) => `<li><b>${c.rank}</b><div><h3>${esc(c.name)}</h3><p>${esc(c.power)}</p>
      <p class="fine">${esc(c.flavour)}</p></div></li>`,
  ).join('')}</ul>`;

export const aboutHtml = (): string => `
  <h2>About Hearsay</h2>
  <p>A micro-round of deduction: one card in hand, and everything you know about the others you
    learned from what they threw away.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
    No accounts, no cookies, no trackers — just anonymous, cookie-less page counts.</p>
  <p class="fine">Playing with friends connects your browsers <b>directly to each other</b> over
    WebRTC. A public relay is used only to introduce them; no game state ever touches a server, and
    there is no server to touch. Connecting does reveal your IP address to the people in your room,
    which is how peer-to-peer works everywhere.</p>
  <p class="fine">Hands are genuinely hidden: one player's browser deals and adjudicates, and sends
    every other browser only what the rules say it may see. If that player closes their tab, whoever
    takes over rebuilds the hearing from the public record plus everyone's own cards — the cards
    still to be drawn are exactly the ones nobody has accounted for, so the hearing carries on with
    the same deck it had.</p>`;

export function menuHtml(mode: ModeId, muted: boolean, best: number): string {
  return `<div class="main-content menu">
    <h1 class="wordmark">Hearsay</h1>
    <p class="tagline">One card in hand. Everything you know about the others, you learned from what
      they threw away.</p>
    <div class="modepick" role="group" aria-label="Mode">
      ${MODE_IDS.map(
        (id) =>
          `<button type="button" class="mode${id === mode ? ' on' : ''}" data-mode="${id}">
             <b>${MODES[id].name}</b><span>${esc(MODES[id].blurb)}</span></button>`,
      ).join('')}
    </div>
    <div class="menubtns">
      <button id="playSolo" class="primary" type="button">Play</button>
      <button id="playFriends" class="secondary" type="button">Play with friends</button>
    </div>
    <p class="bestline">${best > 0 ? `Hearings taken in ${MODES[mode].name}: <b>${best}</b>` : 'No hearing yet in this mode.'}</p>
    <div class="menufoot">
      <button id="howBtn" class="link" type="button">How to play</button>
      <button id="cardsBtn" class="link" type="button">The cards</button>
      <button id="aboutBtn" class="link" type="button">About</button>
      <button id="muteBtn" class="link" type="button" aria-pressed="${muted}">${muted ? 'Sound off' : 'Sound on'}</button>
    </div>
  </div>${FOOTER_HTML}`;
}

/**
 * Open a panel that can always be closed.
 *
 * The backdrop listener is armed on a delay because a panel opened from a pointerup handler is
 * otherwise closed instantly by that same tap's trailing click.
 */
export function openPanel(host: HTMLElement, html: string): void {
  const back = document.createElement('div');
  back.className = 'backdrop';
  back.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">
    <button class="sheetx" type="button" aria-label="Close">&#10005;</button>${html}</div>`;
  host.append(back);
  const panel = back.querySelector('.sheet')!;
  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    back.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  back.querySelector('.sheetx')!.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  setTimeout(() => {
    back.addEventListener('pointerdown', (e) => {
      if (!panel.contains(e.target as Node)) close();
    });
  }, 350);
}

/** One card's detail, opened from the docket — so "what does a Retrial do" is always one tap away. */
export const cardHtml = (rank: number): string => {
  const c = cardOf(rank);
  return `<h2>${esc(c.name)} <i class="crank big">${c.rank}</i></h2>
    <p>${esc(c.power)}</p><p class="fine">${esc(c.flavour)}</p>`;
};

/**
 * The summary.
 *
 * EVERY seat gets a row, with what they actually did — not a name and a number. And the last line is
 * the one card nobody at the table could ever account for: the set-aside. It is the honest answer to
 * "could I have known?", and on the hearings where it was the Verdict, it is the whole story.
 */
export function summaryHtml(pub: PublicState, awards: readonly Award[], me: string, waiting: string): string {
  const byId = new Map(awards.map((a) => [a.id, a]));
  const close = [...pub.events].reverse().find((e) => e.t === 'close');
  const held = new Map<string, number[]>();
  if (close && close.t === 'close') for (const h of close.held) held.set(h.id, h.ranks);

  const rows = [...pub.seats]
    .sort((a, b) => (byId.get(b.id)?.gained ?? 0) - (byId.get(a.id)?.gained ?? 0))
    .map((s) => {
      const a = byId.get(s.id);
      const ranks = held.get(s.id) ?? [];
      const what = s.alive
        ? `held ${ranks.map((r) => `<b>${r}</b> ${esc(rankName(r))}`).join(', ') || '—'}`
        : `out on ${outWord(s.outCause)}${s.outPly ? ` at ply ${s.outPly}` : ''}`;
      return `<li class="res${s.id === me ? ' you' : ''}${a?.won ? ' won' : ''}">
        <div class="res-h"><b>${esc(s.id === me ? 'You' : s.name)}</b>
          <u>${a && a.gained > 0 ? `+${a.gained}` : '0'}</u></div>
        <div class="res-t">
          <span class="${s.alive ? '' : 'bad'}">${what}</span>
          <span>played ${s.discards.map((r) => r).join(' ') || 'nothing'}</span>
          ${a?.backedRight ? '<span class="good">backed the winner</span>' : ''}
          ${s.backed && !a?.backedRight ? '<span>backed wrong</span>' : ''}
        </div>
        <div class="res-s">standing <b>${pub.standing[s.id] ?? 0}</b> of ${pub.target}</div>
      </li>`;
    })
    .join('');

  const aside = revealedAside(pub);
  const missed = aside
    ? `The card set aside — the one nobody could ever account for — was <b>${esc(rankPhrase(aside))}</b>.`
    : 'Every card was accounted for.';

  return `<div class="main-content results">
    <h2>${pub.reason === 'last' ? 'Everyone else is out' : 'The docket ran dry'}</h2>
    ${waiting ? `<p class="fine">${esc(waiting)}</p>` : ''}
    <ol class="reslist">${rows}</ol>
    <p class="missed">${missed}</p>
    <div class="menubtns">
      <button id="againBtn" class="primary" type="button">Next hearing</button>
      <button id="lobbyBtn" class="secondary" type="button" hidden>Back to lobby</button>
      <button id="menuBtn" class="secondary" type="button">Menu</button>
    </div>
    <p class="fine" id="resWait" hidden></p>
    <p class="fine"><button class="link results-feedback" type="button">Something feel wrong? Tell me</button></p>
  </div>${FOOTER_HTML}`;
}

/** The match is over: who took it, and the standing that got them there. */
export function matchOverHtml(pub: PublicState, me: string): string {
  const top = Math.max(...pub.seats.map((s) => pub.standing[s.id] ?? 0));
  const champs = pub.seats.filter((s) => (pub.standing[s.id] ?? 0) === top);
  const iWon = champs.some((s) => s.id === me);
  return `<h2>${iWon ? 'The match is yours' : `${esc(short(champs[0]?.name ?? 'Nobody'))} takes the match`}</h2>
    <p class="fine">${champs.map((s) => `${esc(s.name)} ${pub.standing[s.id] ?? 0}`).join(' · ')}</p>`;
}

const outWord = (cause: string | null): string =>
  cause === 'named' ? 'a call' : cause === 'dismissed' ? 'the stand' : cause === 'verdict' ? 'the Verdict' : 'leaving';
